import { ReactNode } from "react";
import landingBurger from "@/assets/landing-burger-lifestyle.jpg";

/**
 * Full-bleed hero image at the top of the landing page. The value proposition
 * (headline, benefits, social proof) and the signup block (`children`) are laid
 * over it so both stay above the fold.
 *
 * On the dedicated auth pages (/signin, /signup) the headline is kept but the
 * supporting benefits list and social-proof line are dropped to keep those
 * pages clean — visitors there already know what BRRGR is.
 *
 * Swap `SPLASH_IMAGE` to test a different shot — nothing else needs to change.
 */
const SPLASH_IMAGE = landingBurger;
const SPLASH_ALT = "A freshly stacked cheeseburger on a wooden board";

const BENEFITS = [
  "Find the best burgers near you",
  "Keep track of every burger you've tried",
  "Follow reviewers with your taste",
];

interface LandingSplashProps {
  children?: ReactNode;
  /** When set, keeps the headline but hides the benefits bullets and the
   *  "Join 6,000+" social-proof line (dedicated auth pages). */
  hideClutter?: boolean;
}

export const LandingSplash = ({ children, hideClutter }: LandingSplashProps) => (
  <div className="relative w-full overflow-hidden">
    <img
      src={SPLASH_IMAGE}
      alt={SPLASH_ALT}
      className="absolute inset-0 h-full w-full object-cover"
      {...{ fetchpriority: "high" }}
      decoding="async"
    />
    {/* Scrim: keeps the copy legible and blends the image into the page */}
    <div className="absolute inset-0 bg-foreground/70" aria-hidden="true" />
    <div
      className="absolute inset-0 bg-gradient-to-t from-background via-transparent via-[12%] to-transparent"
      aria-hidden="true"
    />

    <div className="relative mx-auto flex w-full max-w-3xl flex-col items-center px-3 md:px-6 pt-5 md:pt-10 pb-12 md:pb-16 text-center">
      <h1 className="text-[1.7rem] leading-[1.15] md:text-5xl font-extrabold tracking-tight md:leading-[1.1] text-background drop-shadow-lg">
        The world's burger guide,
        <br />
        powered by burger lovers
      </h1>

      {!hideClutter && (
        <>
          <ul className="mt-5 md:mt-6 inline-flex flex-col items-start gap-2 text-left text-[0.95rem] md:text-lg text-background/90 drop-shadow">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 md:gap-3 whitespace-nowrap">
                <span
                  className="mt-[0.7em] h-1.5 w-1.5 rounded-full bg-background/70 shrink-0"
                  aria-hidden="true"
                />
                {b}
              </li>
            ))}
          </ul>



          <p className="mt-5 md:mt-7 text-base md:text-lg font-semibold text-background drop-shadow">
            Join 6,000+ burger lovers on BRRGR for free.
          </p>


        </>
      )}

      {children && <div className={`${hideClutter ? "mt-6" : "mt-5"} w-full max-w-md`}>{children}</div>}
    </div>
  </div>
);

export default LandingSplash;
